import { MonthZeroBased } from '@deltasierra/shared';
import { DateTime } from 'luxon';
import * as React from 'react';
import { getMonth } from './CalendarMonth/get-month';

export interface UseCurrentMonthResponse {
    createShiftCurrentMonth: (amount: number) => () => void;
    currentMonth: MonthZeroBased;
    currentYear: number;
    setCurrent: (args: Date) => void;
}

/**
 * This hook tracks a month and year value.  It provides the following functionality:
 * - tracks month and year value
 * - provides a set function to change them individually or together
 * - will change the date automatically if provided a 'from' and/or 'to' date
 *
 * Other hooks can augment this hook when used together, such as the useCalendarMonth hook,
 * which handles formatting as well as grabbing a list of days in a given month/year combination.
 *
 * @example
 *  function MyMonthDisplay() {
 *      const [date, setDate] = React.useState(new Date());
 *      const { currentMonth, currentYear createShiftCurrentMonth } = useCurrentMonth(date);
 *
 *      return (
 *          <div>
 *              <div>
 *                  {`${currentMonth}, ${currentYear}`}
 *              </div>
 *              <button onClick={createShiftCurrentMonth(-1)}>{'-'}</button>
 *              <button onClick={createShiftCurrentMonth(1)}>{'+'}</button>
 *          </div>
 *      );
 *  }
 * @param from - If using a hook that tracks current date, will change month to follow the current date (from)
 * @param to - If using a hook that tracks a date range, put the second date here to change month if it is set
 * @returns The currentMonth (0-11), the current year, and a way to set them manually, and a hof that creates a shifter
 */
export function useCurrentMonth(from?: Date, to?: Date): UseCurrentMonthResponse {
    const [current, setCurrent] = React.useState<Date>(from ?? new Date());

    React.useEffect(() => {
        setCurrent(prev => from ?? prev);
    }, [from]);

    React.useEffect(() => {
        setCurrent(prev => to ?? prev);
    }, [to]);

    const createShiftCurrentMonth = React.useCallback(
        (amount: number): (() => void) =>
            () =>
                setCurrent(prev => DateTime.fromJSDate(prev).plus({ months: amount }).toJSDate()),
        [],
    );

    return {
        createShiftCurrentMonth,
        currentMonth: getMonth(current),
        currentYear: current.getFullYear(),
        setCurrent,
    };
}
